import React from 'react';
import { IconLink } from '@tabler/icons-react';
import type { NestedResponseLink, StructuredResponseAnswer, StructuredResponseGroup } from '../../lib/response-completion';
import { AnimatedPresence } from '../ui/AnimatedPresence';

/** Render frozen response sections in place, including each selected child and its source link. */
export function ResponseAnswerTree({ groups, childLinks, renderAnswers }: {
  groups: StructuredResponseGroup[];
  childLinks: NestedResponseLink[];
  renderAnswers: (answers: StructuredResponseAnswer[]) => React.ReactNode;
}): React.ReactElement {
  return <div className="space-y-8">{groups.map(group => (
    <div key={group.id}>
      {group.title && <div className="mb-4">
        <h4 className="font-semibold text-foreground">{group.title}</h4>
        {group.description && <p className="mt-1 text-sm text-muted-foreground">{group.description}</p>}
      </div>}
      {group.parts ? <div className="space-y-5">{group.parts.map(part => {
        if (part.kind === 'answers') return <React.Fragment key={part.answers[0].fieldId}>{renderAnswers(part.answers)}</React.Fragment>;
        const link = childLinks.find(row => row.interview_session_id === part.sourceId
          && row.parent_block_id === part.blockId);
        return <section key={part.blockId} aria-label={part.title} className="space-y-4 border-s-2 border-border ps-4">
          <div className="flex items-center gap-2">
            <h5 className="font-medium text-foreground">{part.title}</h5>
            <AnimatedPresence show={Boolean(link)}>
              {link && <a href={`/admin/interviews/${encodeURIComponent(link.interview_session_id)}/responses/${encodeURIComponent(link.id)}`}
                aria-label={`Open ${part.title} response`}
                className="inline-flex rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-ring">
                <IconLink size={15} />
              </a>}
            </AnimatedPresence>
          </div>
          {part.sections.map(child => <div key={child.id} className="space-y-3">
            {child.title && <h6 className="text-sm font-medium text-foreground">{child.title}</h6>}
            {renderAnswers(child.answers)}
          </div>)}
        </section>;
      })}</div> : renderAnswers(group.answers)}
    </div>
  ))}</div>;
}
